import {
  Box,
  Center,
  Text,
  CloseButton,
  Indicator,
  Modal,
  rem,
  Stack,
  Tabs,
} from '@mantine/core'
import {
  BookOpenTextIcon,
  ClockCounterClockwiseIcon,
  MedalIcon,
  TrophyIcon,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { BOGGLE_ACHIEVEMENT_DEFINITIONS } from '~/boggle/config/achievements/registry'
import { isBoggleAchievementsPlayerUiEnabled } from '~/boggle/featureFlags'
import { buildBoggleAchievementCareerStats } from '~/boggle/lib/achievements/careerStats'
import {
  getKnownAchievements,
  type BoggleKnownAchievementsView,
} from '~/boggle/lib/achievements/knownAchievements'
import { createEmptyBoggleBadgeProgressProjection } from '~/boggle/progress/badgeProjections'
import type {
  BoggleBadgeProgressProjection,
  BoggleCompletedRun,
  BoggleUnviewedAchievementTier,
  BoggleUnviewedBadge,
  BoggleUnviewedWord,
  BoggleWordStat,
} from '~/boggle/progress/types'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { Achievements } from '~/boggle/ui/achievements/Achievements'
import { Compendium } from '~/boggle/ui/compendium/Compendium'
import { bootstrapCompendiumHubSession } from '~/boggle/ui/hub/bootstrapCompendiumHubSession'
import { bootstrapLexiconHubSession } from '~/boggle/ui/hub/bootstrapLexiconHubSession'
import { RunHistory } from '~/boggle/ui/history/RunHistory'
import {
  useBoggleUnviewedAchievementTierCount,
  useBoggleUnviewedBadgeButtonCount,
} from '~/boggle/ui/unviewed/useBoggleUnviewedCounts'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'
import { Words } from '~/boggle/ui/words/Words'

import classes from './BoggleProgressHubModal.module.css'

export type BoggleProgressHubTab = 'achievements' | 'compendium' | 'history' | 'lexicon'

const HUB_CONTENT_HEIGHT = `min(calc(100dvh - ${rem(120)}), ${rem(700)})`
const HUB_CONTENT_WIDTH = `min(calc(100vw - ${rem(40)}), ${rem(700)})`

const HUB_MODAL_STYLES = {
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column' as const,
    minHeight: 0,
    padding: 0,
    background: 'none',
  },
  content: {
    background: 'transparent',
    border: 'none',
    display: 'flex',
    flexDirection: 'column' as const,
    height: HUB_CONTENT_HEIGHT,
    maxHeight: HUB_CONTENT_HEIGHT,
    maxWidth: HUB_CONTENT_WIDTH,
    overflow: 'visible',
    width: HUB_CONTENT_WIDTH,
  },
  header: { display: 'none' },
  inner: { padding: rem(10) },
} as const

const HUB_TAB_ICON_SIZE = 30

const EMPTY_ACHIEVEMENTS_VIEW: BoggleKnownAchievementsView = {
  achievements: [],
  completedTierCount: 0,
  totalTierCount: 0,
}

const HUB_TAB_STYLES = {
  panel: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    paddingTop: rem(12),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column' as const,
    minHeight: 0,
  },
  tabLabel: {
    '--text-fz': rem(11),
  },
}

function HubTabIcon({ icon, testId }: { icon: typeof MedalIcon; testId: string }) {
  return (
    <Center data-testid={testId}>
      <IconGlyph icon={icon} size={HUB_TAB_ICON_SIZE} weight="duotone" />
    </Center>
  )
}

export function BoggleProgressHubModal({
  activeTab,
  onClose,
  onTabChange,
  opened,
}: {
  activeTab: BoggleProgressHubTab
  onClose: () => void
  onTabChange: (tab: BoggleProgressHubTab) => void
  opened: boolean
}) {
  const { isDevMode } = useBoggleModeSession()
  const achievementsPlayerUiEnabled = isBoggleAchievementsPlayerUiEnabled(isDevMode)
  const { count: durableUnviewedBadgeCount } = useBoggleUnviewedBadgeButtonCount()
  const { count: durableUnviewedAchievementTierCount } =
    useBoggleUnviewedAchievementTierCount()
  const [historySessionKey, setHistorySessionKey] = useState(0)

  const [progress, setProgress] = useState<BoggleBadgeProgressProjection>(() =>
    createEmptyBoggleBadgeProgressProjection(),
  )
  const [sessionUnviewedBadges, setSessionUnviewedBadges] = useState<
    readonly BoggleUnviewedBadge[]
  >([])
  const [compendiumLoaded, setCompendiumLoaded] = useState(false)
  const compendiumTabCount =
    opened && compendiumLoaded ? sessionUnviewedBadges.length : durableUnviewedBadgeCount

  const [wordsLoading, setWordsLoading] = useState(false)
  const [wordsFailed, setWordsFailed] = useState(false)
  const [wordStats, setWordStats] = useState<BoggleWordStat[]>([])
  const [sessionNewWords, setSessionNewWords] = useState<readonly BoggleUnviewedWord[]>(
    [],
  )
  const [lexiconLoaded, setLexiconLoaded] = useState(false)

  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyFailed, setHistoryFailed] = useState(false)
  const [runs, setRuns] = useState<BoggleCompletedRun[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)

  const [achievementsLoading, setAchievementsLoading] = useState(false)
  const [achievementsFailed, setAchievementsFailed] = useState(false)
  const [achievementsLoaded, setAchievementsLoaded] = useState(false)
  const [achievementsView, setAchievementsView] = useState<BoggleKnownAchievementsView>(
    EMPTY_ACHIEVEMENTS_VIEW,
  )
  const [sessionUnviewedAchievementTiers, setSessionUnviewedAchievementTiers] = useState<
    readonly BoggleUnviewedAchievementTier[]
  >([])
  const achievementsTabCount =
    opened && achievementsLoaded
      ? sessionUnviewedAchievementTiers.length
      : durableUnviewedAchievementTierCount

  const compendiumLoadStarted = useRef(false)
  const lexiconLoadStarted = useRef(false)
  const lexiconAcknowledged = useRef(false)
  const historyLoadStarted = useRef(false)
  const achievementsLoadSeq = useRef(0)
  const prevHubTabRef = useRef<BoggleProgressHubTab | null>(null)
  const wasOpenedRef = useRef(false)

  const resetHubState = useCallback(() => {
    setCompendiumLoaded(false)
    setLexiconLoaded(false)
    setHistoryLoaded(false)
    setSessionUnviewedBadges([])
    setSessionNewWords([])
    setWordsLoading(false)
    setWordsFailed(false)
    setWordStats([])
    setHistoryLoading(false)
    setHistoryFailed(false)
    setRuns([])
    setAchievementsLoaded(false)
    setAchievementsLoading(false)
    setAchievementsFailed(false)
    setAchievementsView(EMPTY_ACHIEVEMENTS_VIEW)
    setSessionUnviewedAchievementTiers([])
    compendiumLoadStarted.current = false
    lexiconLoadStarted.current = false
    lexiconAcknowledged.current = false
    historyLoadStarted.current = false
    achievementsLoadSeq.current += 1
    setHistorySessionKey(key => key + 1)
  }, [])

  useEffect(() => {
    if (opened) {
      wasOpenedRef.current = true
      return
    }

    prevHubTabRef.current = null

    if (!wasOpenedRef.current) {
      return
    }

    wasOpenedRef.current = false
    void (async () => {
      const repository = getBoggleProgressRepository()
      if (activeTab === 'lexicon') {
        await repository.acknowledgeUnviewedWords()
      }
      resetHubState()
    })()
  }, [activeTab, opened, resetHubState])

  useEffect(() => {
    if (!opened) {
      return
    }
    prevHubTabRef.current = activeTab
  }, [activeTab, opened])

  useEffect(() => {
    if (!opened || compendiumLoadStarted.current) {
      return
    }
    compendiumLoadStarted.current = true

    let active = true
    void (async () => {
      try {
        const repository = getBoggleProgressRepository()
        const { progress: nextProgress, sessionUnviewedBadges: unviewedBadges } =
          await bootstrapCompendiumHubSession(repository)
        if (!active) {
          return
        }
        setProgress(nextProgress)
        setSessionUnviewedBadges(unviewedBadges)
        setCompendiumLoaded(true)
      } catch (error) {
        console.error('[boggle] failed to load compendium progress', error)
        if (active) {
          setCompendiumLoaded(true)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [opened])

  useEffect(() => {
    if (!opened || lexiconLoadStarted.current) {
      return
    }
    lexiconLoadStarted.current = true

    let cancelled = false
    setWordsLoading(true)
    setWordsFailed(false)
    void (async () => {
      try {
        const repository = getBoggleProgressRepository()
        const { sessionNewWords: unviewedWords, wordStats: nextStats } =
          await bootstrapLexiconHubSession(repository)
        if (cancelled) {
          return
        }
        setWordStats(nextStats)
        setSessionNewWords(unviewedWords)
        setLexiconLoaded(true)
      } catch {
        if (!cancelled) {
          setWordsFailed(true)
          setWordStats([])
          setSessionNewWords([])
          setLexiconLoaded(true)
        }
      } finally {
        if (!cancelled) {
          setWordsLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [opened])

  useEffect(() => {
    if (
      !opened ||
      activeTab !== 'lexicon' ||
      !lexiconLoaded ||
      lexiconAcknowledged.current
    ) {
      return
    }
    lexiconAcknowledged.current = true

    void getBoggleProgressRepository()
      .acknowledgeUnviewedWords()
      .catch(error => {
        console.error('[boggle] failed to acknowledge unviewed words', error)
      })
  }, [activeTab, lexiconLoaded, opened])

  useEffect(() => {
    if (!opened || historyLoadStarted.current) {
      return
    }
    historyLoadStarted.current = true

    let cancelled = false
    setHistoryLoading(true)
    setHistoryFailed(false)
    void getBoggleProgressRepository()
      .getCompletedRuns()
      .then(nextRuns => {
        if (!cancelled) {
          setRuns(nextRuns)
          setHistoryLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryFailed(true)
          setRuns([])
          setHistoryLoaded(true)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHistoryLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [opened])

  const loadAchievements = useCallback(() => {
    const loadSeq = ++achievementsLoadSeq.current
    setAchievementsLoading(true)
    setAchievementsFailed(false)
    setAchievementsLoaded(true)
    void (async () => {
      try {
        const repository = getBoggleProgressRepository()
        const achievementState = await repository.getAchievementState()
        const [badgeProgress, completedRuns, wordOccurrences, unviewedAchievementTiers] =
          await Promise.all([
            repository.getBadgeProgress(),
            repository.getCompletedRuns(),
            repository.getWordOccurrences(),
            repository.acknowledgeUnviewedAchievementTiers(),
          ])
        if (loadSeq !== achievementsLoadSeq.current) {
          return
        }
        const careerStats = buildBoggleAchievementCareerStats({
          completedRuns,
          wordProjections: {
            wordOccurrences,
          },
        })
        setAchievementsView(
          getKnownAchievements(
            achievementState,
            BOGGLE_ACHIEVEMENT_DEFINITIONS,
            careerStats,
            badgeProgress,
          ),
        )
        setSessionUnviewedAchievementTiers(unviewedAchievementTiers)
      } catch (error) {
        console.error('[boggle] failed to load achievements', error)
        if (loadSeq === achievementsLoadSeq.current) {
          setAchievementsFailed(true)
          setAchievementsView(EMPTY_ACHIEVEMENTS_VIEW)
          setSessionUnviewedAchievementTiers([])
        }
      } finally {
        if (loadSeq === achievementsLoadSeq.current) {
          setAchievementsLoading(false)
        }
      }
    })()
  }, [])

  useEffect(() => {
    if (!achievementsPlayerUiEnabled || !opened || activeTab !== 'achievements') {
      return
    }
    loadAchievements()
  }, [achievementsPlayerUiEnabled, activeTab, loadAchievements, opened])

  const markBadgeViewed = (badgeId: BoggleUnviewedBadge['badgeId']) => {
    setSessionUnviewedBadges(current =>
      current.filter(entry => entry.badgeId !== badgeId),
    )
  }

  const markWordViewed = (normalizedWord: string) => {
    setSessionNewWords(current =>
      current.filter(entry => entry.normalizedWord !== normalizedWord),
    )
  }

  const handleTabChange = (value: string | null) => {
    if (value === 'achievements' && !achievementsPlayerUiEnabled) {
      return
    }
    if (
      value === 'achievements' ||
      value === 'compendium' ||
      value === 'lexicon' ||
      value === 'history'
    ) {
      if (value === 'history' && value === activeTab) {
        setHistorySessionKey(key => key + 1)
      }
      if (value === 'achievements' && value === activeTab) {
        loadAchievements()
      }
      onTabChange(value)
    }
  }

  return (
    <Modal
      centered
      data-testid="boggle-progress-hub-modal"
      keepMounted={false}
      onClose={onClose}
      opened={opened}
      padding={0}
      radius="md"
      styles={HUB_MODAL_STYLES}
      title={null}
      withCloseButton={false}
    >
      <Tabs
        classNames={{ list: classes.tabList, tab: classes.tab }}
        onChange={handleTabChange}
        styles={HUB_TAB_STYLES}
        value={activeTab}
        variant="unstyled"
        keepMounted={true}
      >
        <Box pos="relative" w="100%">
          <Tabs.List>
            <Indicator
              color="red"
              disabled={compendiumTabCount <= 0}
              inline
              label={compendiumTabCount > 99 ? '99+' : compendiumTabCount}
              offset={6}
              position="top-end"
              processing={false}
              size={rem(20)}
              zIndex={3}
            >
              <Tabs.Tab
                aria-label="Compendium"
                data-testid="boggle-progress-hub-tab-compendium"
                value="compendium"
              >
                <Stack gap={6}>
                  <HubTabIcon
                    icon={MedalIcon}
                    testId="boggle-progress-hub-tab-compendium-icon"
                  />
                  <Text tt="uppercase">Badges</Text>
                </Stack>
              </Tabs.Tab>
            </Indicator>
            {achievementsPlayerUiEnabled && (
              <Indicator
                color="red"
                disabled={achievementsTabCount <= 0}
                inline
                label={achievementsTabCount > 99 ? '99+' : achievementsTabCount}
                offset={6}
                position="top-end"
                processing={false}
                size={rem(20)}
                zIndex={3}
              >
                <Tabs.Tab
                  aria-label="Achievements"
                  data-testid="boggle-progress-hub-tab-achievements"
                  onClick={() => {
                    if (activeTab === 'achievements') {
                      loadAchievements()
                    }
                  }}
                  value="achievements"
                >
                  <Stack gap={6}>
                    <HubTabIcon
                      icon={TrophyIcon}
                      testId="boggle-progress-hub-tab-achievements-icon"
                    />
                    <Text tt="uppercase">{'Trophies'}</Text>
                  </Stack>
                </Tabs.Tab>
              </Indicator>
            )}
            <Tabs.Tab
              aria-label="Lexicon"
              data-testid="boggle-progress-hub-tab-lexicon"
              value="lexicon"
            >
              <Stack gap={6}>
                <HubTabIcon
                  icon={BookOpenTextIcon}
                  testId="boggle-progress-hub-tab-lexicon-icon"
                />
                <Text tt="uppercase">Words</Text>
              </Stack>
            </Tabs.Tab>
            <Tabs.Tab
              aria-label="Run history"
              data-testid="boggle-progress-hub-tab-history"
              value="history"
            >
              <Stack gap={6}>
                <HubTabIcon
                  icon={ClockCounterClockwiseIcon}
                  testId="boggle-progress-hub-tab-history-icon"
                />
                <Text tt="uppercase">History</Text>
              </Stack>
            </Tabs.Tab>
          </Tabs.List>
          <CloseButton
            aria-label="Close progress hub"
            c="rgba(255,255,255,0.5)"
            data-testid="boggle-progress-hub-close"
            iconSize={rem(28)}
            onClick={onClose}
            pos="absolute"
            right={8}
            top={8}
            variant="transparent"
          />
        </Box>

        <Stack
          className="modalBackground"
          h="100%"
          mih={0}
          style={{
            overflow: 'hidden',
            border: `${rem(1)} solid var(--mantine-color-dark-6)`,
            boxShadow: `0 ${rem(12)} ${rem(40)} rgba(0, 0, 0, 0.55)`,
            borderRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Tabs.Panel value="compendium">
            <Box flex={1} h="100%" mih={0} style={{ overflow: 'hidden' }}>
              {compendiumLoaded ? (
                <Compendium
                  onBadgeViewed={markBadgeViewed}
                  progress={progress}
                  revealHiddenLockedBadges={isDevMode}
                  sessionUnviewedBadges={sessionUnviewedBadges}
                />
              ) : null}
            </Box>
          </Tabs.Panel>
          <Tabs.Panel value="lexicon">
            <Box flex={1} h="100%" mih={0} style={{ overflow: 'hidden' }}>
              {lexiconLoaded ? (
                <Words
                  failed={wordsFailed}
                  loading={wordsLoading}
                  onWordViewed={markWordViewed}
                  sessionNewWords={sessionNewWords}
                  stats={wordStats}
                />
              ) : null}
            </Box>
          </Tabs.Panel>
          {achievementsPlayerUiEnabled ? (
            <Tabs.Panel value="achievements">
              <Box flex={1} h="100%" mih={0} style={{ overflow: 'hidden' }}>
                {achievementsLoaded || achievementsLoading ? (
                  <Achievements
                    failed={achievementsFailed}
                    loading={achievementsLoading}
                    onViewBadges={() => onTabChange('compendium')}
                    sessionUnviewedAchievementTiers={sessionUnviewedAchievementTiers}
                    view={achievementsView}
                  />
                ) : null}
              </Box>
            </Tabs.Panel>
          ) : null}
          <Tabs.Panel value="history">
            <Box flex={1} h="100%" mih={0} style={{ overflow: 'hidden' }}>
              {historyLoaded ? (
                <RunHistory
                  failed={historyFailed}
                  key={historySessionKey}
                  loading={historyLoading}
                  runs={runs}
                />
              ) : null}
            </Box>
          </Tabs.Panel>
        </Stack>
      </Tabs>
    </Modal>
  )
}
